/**
 * Tags as a list, whatever shape they arrived in.
 *
 * ## Why this exists
 *
 * The backend stores and returns tags as one comma-separated string -
 * `"content,content"`, `"Intro,live_video"` - while the frontend was written, and
 * typed, as though they were an array. A non-empty string satisfies a
 * `tags && tags.length > 0` guard and then has no `.map`, so the player page threw
 * `tags.map is not a function` and unmounted entirely for every item that had tags.
 * Four of the five items in the catalogue did.
 *
 * The TypeScript type said `string[]`, which is exactly why nothing caught it: the
 * type described what the code wished it received, and the compiler has no way to
 * check that against an HTTP response.
 *
 * So tags are normalised once, where data enters, rather than defended against at
 * each of the places that render them.
 *
 * Duplicates are removed because the data genuinely contains them (`"content,content"`
 * is real), and rendering `key={tag}` for a repeated tag gives React duplicate keys.
 */
export function toTagList(raw: unknown): string[] {
  let parts: unknown[];

  if (Array.isArray(raw)) {
    parts = raw;
  } else if (typeof raw === 'string') {
    parts = raw.split(',');
  } else {
    return [];
  }

  const seen = new Set<string>();
  const tags: string[] = [];
  for (const part of parts) {
    if (typeof part !== 'string') continue;
    const tag = part.trim();
    if (!tag) continue;
    // Case-insensitive, so "Intro" and "intro" do not render as two chips.
    const key = tag.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    tags.push(tag);
  }
  return tags;
}
